"use client";

import { useState, useEffect } from "react";
import { useIsMobile } from "../hooks/use-mobile";
import { TabbedContent } from "../components/home_components/tabbed-content";
import NotificationCard from "../components/home_components/NotificationCard";
import PromotionCard from "../components/home_components/PromotionCard";
import SocialPostCard from "../components/home_components/SocialPostCard";
import HomePromotionCard from "../components/home_components/HomePromotionCard";
import HappeningCard from "../components/home_components/HappeningCard";
import MainLayout from "../components/MainLayout";
import HomeRightSidebar from "../components/home_components/HomeRightSidebar";
import followService from "../services/follow";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchMasterCities,
  retrieveMasterCity
} from "../services/locationServices";
import { fetchCommunityEvents } from "../services/events";
import { convertDateToObject } from "../lib/utils";
import { getPostings } from "../services/scoops";
import { isAuthenticated, slugify } from "../utils/helpers";
import NewsCard from "../components/home_components/NewsCard";
import { getNews } from "../services/news";
import { jwtDecode } from "jwt-decode";
import CreateScoopForm2 from "../components/scoops_components/CreateScoopForm2";
import { getQuestions } from "../services/poll";
import QuestionCard from "../components/home_components/QuestionCard";
import EventForm from "../components/event_components/EventForm";

// Import skeleton components
import PostSkeleton from "../components/skeletons/PostSkeleton";
import NewsCardSkeleton from "../components/skeletons/NewsCardSkeleton";
import EventCardSkeleton from "../components/skeletons/EventCardSkeleton";
import QuestionCardSkeleton from "../components/skeletons/QuestionCardSkeleton";
import GallynomicsCard from "../components/gallyonomics_components/card";
import {
  listGallyoSentimentArticle,
  listMarketPulseArticle
} from "../services/article";

function HomePage2() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [isFirstCardVisible, setIsFirstCardVisible] = useState(true);
  const [isSecondCardVisible, setIsSecondCardVisible] = useState(false);
  const isMobile = useIsMobile();

  /* ---------------------- Social Media Posts Section ---------------------- */
  const [postings, setPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ---------------------- News Section ---------------------- */
  const [newsData, setNewsData] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);

  const [marketPulse, setMarketPulse] = useState(null);
  const [gallyoSentiment, setGallyoSentiment] = useState(null);

  const [articles, setArticles] = useState(null);
  useEffect(() => {
    async function fetchAllArticles() {
      try {
        const marketResponse = await listMarketPulseArticle({
          offset: 0,
          limit: 1
        });
        const marketPulses = marketResponse.map(rp => {
          return { ...rp, category: { name: "Market Pulse" } };
        });
        setMarketPulse(marketPulses);

        const sentimentResponse = await listGallyoSentimentArticle({
          offset: 0,
          limit: 1
        });
        const marketSentiment = sentimentResponse.map(rp => {
          return { ...rp, category: { name: "Gallyo Sentiment" } };
        });

        setGallyoSentiment(marketSentiment);

        setArticles([
          ...marketPulses,
          ...marketSentiment
        ]);
      } catch (error) {
        console.error("Error while trying to fetch market pulses: ", error);
      }
    }

    fetchAllArticles();
  }, []);

  useEffect(() => {
    async function fetchNews() {
      try {
        setNewsLoading(true);
        const news = await getNews();
        setNewsData(news[0].news.slice(0, 4));
      } catch (error) {
        console.error("Error while fetching news", error);
      } finally {
        setNewsLoading(false);
      }
    }
    fetchNews();
  }, []);

  const [postFetcher, setPostFetcher] = useState(false);

  // Function to fetch posts
  const fetchPosts = async () => {
    setLoading(true);
    setError(null);

    const geohash = localStorage.getItem("geohash") || "9v6m";
    const requestBody = { geohash, offset: 0 };

    try {
      const response = await getPostings(requestBody);
      setPostings((response.posts || []).slice(0, 5));
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ?? error.data?.message ?? error;
      setError(errorMessage);
      console.error("Error while retrievig postings", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(
    () => {
      fetchPosts();
    },
    [postFetcher]
  );

  // Function to handle close button click
  const handleCloseClick = () => {
    setIsFirstCardVisible(false);
    setIsSecondCardVisible(true);
  };

  /* ---------------------- Retrieving All Master Cities -------------------- */
  const [masterCities, setMasterCities] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState({
    state: "",
    city: ""
  });
  const [communityEvents, setCommunityEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  /* ---------------------- Questions Section ---------------------- */
  const [questions, setQuestions] = useState(null);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  useEffect(
    () => {
      async function fetchQuestions() {
        try {
          setQuestionsLoading(true);
          const geohash = localStorage.getItem("geohash") || "9v6m";
          const questionsResponse = await getQuestions(geohash);
          setQuestions(questionsResponse.data);
        } catch (error) {
          console.error("Error while fetching questions : ", error);
        } finally {
          setQuestionsLoading(false);
        }
      }
      if (!questions) {
        fetchQuestions();
      }
    },
    [questions]
  );

  useEffect(() => {
    async function fetchMasterCity() {
      try {
        if (isProcessing) {
          return;
        }
        setIsProcessing(true);
        const citiesResponse = await fetchMasterCities();
        setMasterCities(citiesResponse.data);
      } catch (error) {
        console.error("Error while getting master cities", error);
      } finally {
        setIsProcessing(false);
      }
    }
    fetchMasterCity();
  }, []);

  // Getting Community Events around me
  useEffect(() => {
    async function fetchCommunityEvent() {
      try {
        setEventsLoading(true);
        setIsProcessing(true);
        const geohash = localStorage.getItem("geohash") ?? "9v6m";
        const payload = { geohash: geohash };
        const location = await fetchMasterCities(payload);
        const eventsResponse = await fetchCommunityEvents({
          state: location.data[0].state,
          city: location.data[0].city
        });
        setCommunityEvents(eventsResponse.data);
      } catch (error) {
        console.error("Error while fetching community events", error);
      } finally {
        setEventsLoading(false);
        setIsProcessing(false);
      }
    }
    fetchCommunityEvent();
  }, []);

  async function handleLocationChange() {
    const payload = {
      latitude: 0.0,
      longitude: 0.0,
      city: selectedLocation.city,
      state: selectedLocation.state
    };
    try {
      if (isProcessing) {
        return;
      }
      setIsProcessing(true);
      const cityResponse = await retrieveMasterCity(payload);
      // API returns { status, geohash: { geohash, lat, long, city, state } }
      if (cityResponse?.geohash && typeof cityResponse.geohash === "object") {
        localStorage.setItem("geohash", cityResponse.geohash.geohash);
        setMasterCities(cityResponse.geohash);
      }
    } catch (error) {
      console.error("Error while changing location", error);
    } finally {
      setIsProcessing(false);
    }
  }

  const [followingUsers, setFollowingUsers] = useState([]);

  useEffect(() => {
    async function fetchFollowingUsers() {
      const token = localStorage.getItem("access_token");
      if (!token) {
        return;
      }
      const decodedToken = jwtDecode(token);
      const followings = await followService.handleGetUserFollowings(
        decodedToken.userid
      );
      const followingUsersId = followings.data.map(
        follow => follow.followeduserid
      );
      setFollowingUsers(followingUsersId);
    }
    fetchFollowingUsers();
  }, []);

  // Render skeleton loaders for posts
  const renderPostSkeletons = () => {
    return Array.from({ length: 3 }, (_, index) => (
      <PostSkeleton key={`post-skeleton-${index}`} />
    ));
  };

  // Render skeleton loaders for news
  const renderNewsSkeletons = () => {
    return Array.from({ length: 4 }, (_, index) => (
      <NewsCardSkeleton key={`news-skeleton-${index}`} isMobile={isMobile} />
    ));
  };

  // Render skeleton loaders for events
  const renderEventSkeletons = () => {
    return Array.from({ length: isMobile ? 3 : 2 }, (_, index) => (
      <EventCardSkeleton key={`event-skeleton-${index}`} isMobile={isMobile} />
    ));
  };

  // Render skeleton loaders for questions
  const renderQuestionSkeletons = () => {
    return Array.from({ length: 3 }, (_, index) => (
      <QuestionCardSkeleton key={`question-skeleton-${index}`} />
    ));
  };

  // Render the home page content
  const renderHomeContent = () => {
    if (isMobile) {
      return (
        <div className="p-4">
          {/* First Card - Initially visible with close button */}
          {isFirstCardVisible && (
            <NotificationCard onClose={handleCloseClick} isMobile={isMobile} />
          )}

          {/* Second Card - Initially invisible, appears when first card is closed */}
          {isSecondCardVisible && <HomePromotionCard />}

          <TabbedContent />

          <section className="mb-8">
            <div className="flex justify-between items-center gap-2 mb-4 pt-3 px-1">
              <h1 className="text-xl font-semibold font-fraunces truncate">
                Scoops Around You
              </h1>
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to="/scoops"
                  className="text-xs text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap"
                >
                  View All
                </Link>
                <Link
                  to="/groups"
                  className="text-xs px-3 py-1.5 bg-[#7B189F] text-white rounded-md font-medium shadow hover:bg-[#8F1DB8] transition-all duration-200 whitespace-nowrap"
                >
                  Groups
                </Link>
              </div>
            </div>
            <CreateScoopForm2
              refreshFeed={() => setPostFetcher(prev => !prev)}
            />

            {/* Social Posts with Skeleton Loading */}
            <div className="space-y-4">
              {loading
                ? renderPostSkeletons()
                : postings.map((post, index) => (
                    <div key={post.id || post._id || index} className="w-full">
                      {followingUsers && (
                        <SocialPostCard
                          postFetcher={setPostFetcher}
                          followingUsers={followingUsers}
                          post={post}
                          isMobile={isMobile}
                          images={
                            post.photopath ? post.photopath.split(",") : []
                          }
                        />
                      )}
                    </div>
                  ))}
            </div>

            {!loading && (
              <Link to="/scoops" className="block mt-4">
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg shadow-md p-4 border border-purple-100 hover:shadow-xl transition-all duration-300 flex justify-between items-center group">
                  <div className="flex items-center">
                    <div className="bg-purple-100 p-2 rounded-full mr-3 group-hover:bg-purple-200 transition-colors">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                    <span className="font-medium text-gray-800 group-hover:text-purple-800 transition-colors">
                      Discover more scoops
                    </span>
                  </div>
                  <span className="text-purple-600 text-xl group-hover:translate-x-1 transition-transform">
                    →
                  </span>
                </div>
              </Link>
            )}
          </section>

          <PromotionCard />
          {/* News Section with Skeleton Loading */}
          <section className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-semibold font-fraunces">
                Gallionomics
              </h2>
              <Link
                to="/gallionomics"
                className="text-sm text-gray-600 flex items-center"
              >
                Explore More <span className="ml-1">→</span>
              </Link>
            </div>
            <div className="overflow-x-auto pb-2">
              <div className="flex space-x-4">
                {!marketPulse && !gallyoSentiment ? (
                  renderNewsSkeletons()
                ) : (
                  <>
                    {articles &&
                      articles.map(article => (
                        <GallynomicsCard key={article.id || article.article_title} article={article} />
                      ))}

                    <Link
                      to="/gallionomics"
                      className="flex-shrink-0 min-w-[220px]  h-[330px]"
                    >
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg shadow-md p-4 border border-blue-100 hover:shadow-xl transition-all duration-300 flex flex-col justify-center items-center h-full group">
                        <div className="bg-blue-100 p-2 rounded-full mb-2 group-hover:bg-blue-200 transition-colors">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-blue-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                            />
                          </svg>
                        </div>
                        <span className="font-medium text-gray-800 group-hover:text-blue-800 transition-colors">
                          Explore more gallionomics
                        </span>
                        <span className="text-blue-600 text-xl mt-1 group-hover:translate-y-1 transition-transform">
                          ↓
                        </span>
                      </div>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* News Section with Skeleton Loading */}
          <section className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-semibold font-fraunces">
                Local Buzz
              </h2>
              <Link
                to="/news"
                className="text-sm text-gray-600 flex items-center"
              >
                Explore More <span className="ml-1">→</span>
              </Link>
            </div>
            <div className="overflow-x-auto pb-2">
              <div className="flex space-x-4">
                {newsLoading ? (
                  renderNewsSkeletons()
                ) : (
                  <>
                    {newsData &&
                      newsData.map((item, index) => (
                        <NewsCard
                          key={index}
                          item={item}
                          index={index}
                          isMobile="true"
                        />
                      ))}
                    <Link
                      to="/news"
                      className="flex-shrink-0 min-w-[220px] h-64"
                    >
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg shadow-md p-4 border border-blue-100 hover:shadow-xl transition-all duration-300 flex flex-col justify-center items-center h-full group">
                        <div className="bg-blue-100 p-2 rounded-full mb-2 group-hover:bg-blue-200 transition-colors">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-blue-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                            />
                          </svg>
                        </div>
                        <span className="font-medium text-gray-800 group-hover:text-blue-800 transition-colors">
                          Explore more news
                        </span>
                        <span className="text-blue-600 text-xl mt-1 group-hover:translate-y-1 transition-transform">
                          ↓
                        </span>
                      </div>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* Questions Section with Skeleton Loading */}
          <section className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-semibold font-fraunces">
                Community Questions
              </h2>
            </div>
            <div className="overflow-x-auto pb-2">
              <div className="flex space-x-4">
                {questionsLoading ? (
                  renderQuestionSkeletons()
                ) : questions && questions.length > 0 ? (
                  questions.map(question => (
                    <div
                      key={question.question_id}
                      className="flex-shrink-0 min-w-[280px]"
                    >
                      <QuestionCard question={question} />
                    </div>
                  ))
                ) : (
                  <div className="w-full text-center py-4 text-gray-500">
                    No community questions available
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Banner Images Section - Mobile */}
          <section className="mb-6">
            <div className="flex-none w-125 snap-center">
              <div className="rounded-lg overflow-hidden h-62">
                <img
                  src="/images/image0.webp"
                  alt="VR headset promotional image showing virtual reality experience"
                  className="w-full h-full object-contain"
                  onClick={() => {
                    if (isAuthenticated()) {
                      setShowForm(true);
                    } else {
                      localStorage.setItem("target", window.location.pathname);
                      navigate("/signin");
                    }
                  }}
                />
              </div>
            </div>
          </section>

          {/* Events Section with Skeleton Loading */}
          <section className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-xl font-semibold font-fraunces">
                Happening Near You
              </h2>
            </div>
            <div className="flex overflow-x-auto gap-4 pb-4">
              {eventsLoading ? (
                renderEventSkeletons()
              ) : (
                <>
                  {communityEvents &&
                    communityEvents.map((event, index) => (
                      <div key={event.id || index} className="flex-shrink-0">
                        <HappeningCard
                          key={event.id || index}
                          image={
                            event.cover_image ||
                            "https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png"
                          }
                          imageAlt="Gender Reveal Party"
                          category={{
                            name: "Community",
                            icon: "👨‍👩‍👧‍👦",
                            bgColor: "bg-blue-100",
                            textColor: "text-blue-800"
                          }}
                          link={`/eventdetail/community/${slugify(
                            event.name
                          )}/${event.id}`}
                          title={event.name}
                          description={event.description}
                          time={event.time}
                          location={event.location}
                          date={
                            convertDateToObject(event.date) ?? event.event_date
                          }
                          isMobile={true}
                          onGetDirection={() =>
                            console.log(
                              "Get direction clicked for Gender Reveal Party"
                            )
                          }
                        />
                      </div>
                    ))}
                  <Link to="/events" className="flex-shrink-0 min-w-[220px]">
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg shadow-md p-4 border border-amber-100 hover:shadow-xl transition-all duration-300 flex flex-col justify-center items-center h-full group">
                      <div className="bg-amber-100 p-2 rounded-full mb-2 group-hover:bg-amber-200 transition-colors">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-amber-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <span className="font-medium text-gray-800 group-hover:text-amber-800 transition-colors">
                        Discover more events
                      </span>
                      <span className="text-amber-600 text-xl mt-1 group-hover:translate-y-1 transition-transform">
                        ↓
                      </span>
                    </div>
                  </Link>
                </>
              )}
            </div>
          </section>
          {showForm && (
            <EventForm onClose={() => setShowForm(false)} pe={true} />
          )}
        </div>
      );
    }

    // Desktop content with skeleton loaders
    return (
      <>
        {/* First Card - Initially visible with close button */}
        {isFirstCardVisible && <NotificationCard onClose={handleCloseClick} />}
        {/* Second Card - Initially invisible, appears when first card is closed */}
        {isSecondCardVisible && <HomePromotionCard />}

        <div className="flex pt-4 flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 ">
          <h1 className="text-xl lg:text-2xl font-bold font-fraunces">
            Scoops Around You
          </h1>
          <div className="flex items-center gap-3">
            <Link
              to="/scoops"
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center transition-colors"
            >
              Explore More
            </Link>
            <Link
              to="/groups"
              className="px-3 py-1.5 bg-[#7B189F] text-white rounded-md font-medium shadow-lg hover:bg-[#8F1DB8] transition-all duration-200 transform hover:scale-105 flex items-center"
            >
              Browse Groups
            </Link>
          </div>
        </div>

        <CreateScoopForm2 refreshFeed={() => setPostFetcher(prev => !prev)} />

        {/* Posts with Skeleton Loading */}
        {loading
          ? renderPostSkeletons()
          : postings.map((post, index) => (
              <div key={post.id || post._id || index}>
                <SocialPostCard
                  postFetcher={setPostFetcher}
                  followingUsers={followingUsers}
                  post={post}
                  username={post.username}
                  images={post.photopath ? post.photopath.split(",") : []}
                />
              </div>
            ))}

        {!loading && (
          <Link to="/scoops" className="block my-6">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg shadow-sm p-5 border border-purple-100 hover:shadow-md transition-all duration-300 flex justify-between items-center group">
              <div className="flex items-center">
                <div className="bg-purple-100 p-3 rounded-full mr-4 group-hover:bg-purple-200 transition-colors">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
                <span className="font-medium text-gray-800 text-lg group-hover:text-purple-800 transition-colors">
                  Discover more scoops
                </span>
              </div>
              <span className="text-purple-600 text-xl group-hover:translate-x-1 transition-transform">
                →
              </span>
            </div>
          </Link>
        )}

        {/* News Section with Skeleton Loading */}
        <section className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-xl lg:text-2xl font-bold mb-4 mt-3 font-fraunces">
              Gallionomics
            </h1>
            <Link
              to="/gallionomics"
              className="text-sm text-gray-600 flex items-center"
            >
              Explore More <span className="ml-1">→</span>
            </Link>
          </div>
          <div
            className="overflow-x-auto pb-2"
            style={{ scrollbarWidth: "none" }}
          >
            <div className="flex space-x-4">
              {!marketPulse && !gallyoSentiment ? (
                renderNewsSkeletons()
              ) : (
                <>
                  {articles &&
                    articles.map(article => (
                      <GallynomicsCard key={article.id || article.article_title} article={article} />
                    ))}

                  <Link
                    to="/gallionomics"
                    className="flex-shrink-0 min-w-[220px]  h-[330px]"
                  >
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg shadow-md p-4 border border-blue-100 hover:shadow-xl transition-all duration-300 flex flex-col justify-center items-center h-full group">
                      <div className="bg-blue-100 p-2 rounded-full mb-2 group-hover:bg-blue-200 transition-colors">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                          />
                        </svg>
                      </div>
                      <span className="font-medium text-gray-800 group-hover:text-blue-800 transition-colors">
                        Explore more gallionomics
                      </span>
                      <span className="text-blue-600 text-xl mt-1 group-hover:translate-y-1 transition-transform">
                        ↓
                      </span>
                    </div>
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>

        {/* News Section with Skeleton Loading */}
        <section className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-xl lg:text-2xl font-bold mb-4 mt-3 font-fraunces">
              Local Buzz
            </h1>
            <Link
              to="/news"
              className="text-sm text-gray-600 flex items-center"
            >
              Explore More <span className="ml-1">→</span>
            </Link>
          </div>
          <div
            className="overflow-x-auto pb-2"
            style={{ scrollbarWidth: "none" }}
          >
            <div className="flex space-x-4">
              {newsLoading ? (
                renderNewsSkeletons()
              ) : (
                <>
                  {newsData &&
                    newsData.map((item, index) => (
                      <NewsCard
                        key={index}
                        item={item}
                        index={index}
                        isMobile="true"
                      />
                    ))}
                  <Link to="/news" className="flex-shrink-0 min-w-[280px] h-64">
                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg shadow-sm p-5 border border-blue-100 hover:shadow-md transition-all duration-300 flex flex-col justify-center items-center h-full group">
                      <div className="bg-blue-100 p-3 rounded-full mb-3 group-hover:bg-blue-200 transition-colors">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                          />
                        </svg>
                      </div>
                      <span className="font-medium text-gray-800 text-lg group-hover:text-blue-800 transition-colors">
                        Explore more news
                      </span>
                      <span className="text-blue-600 text-xl mt-2 group-hover:translate-y-1 transition-transform">
                        ↓
                      </span>
                    </div>
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>

        {/* Events Section with Skeleton Loading */}
        <h1 className="text-xl lg:text-2xl font-bold mb-4 mt-3 font-fraunces">
          Happening Near You
        </h1>

        {eventsLoading ? (
          <div className="space-y-4">{renderEventSkeletons()}</div>
        ) : (
          <>
            {communityEvents &&
              communityEvents.map((event, index) => (
                <HappeningCard
                  key={event.id || index}
                  image={
                    event.cover_image ||
                    "https://developers.elementor.com/docs/assets/img/elementor-placeholder-image.png"
                  }
                  imageAlt="Gender Reveal Party"
                  category={{
                    name: "Community",
                    icon: "👨‍👩‍👧‍👦",
                    bgColor: "bg-blue-100",
                    textColor: "text-blue-800"
                  }}
                  title={event.name}
                  description={event.description}
                  time={event.time}
                  location={event.location || event.venue_full_address}
                  date={convertDateToObject(event.date) ?? event.event_date}
                  isMobile={false}
                  onGetDirection={() =>
                    console.log("Get direction clicked for Gender Reveal Party")
                  }
                  link={`/eventdetail/community/${slugify(event.name)}/${
                    event.id
                  }`}
                />
              ))}
            <Link to="/events" className="block my-6">
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg shadow-sm p-5 border border-amber-100 hover:shadow-md transition-all duration-300 flex justify-between items-center group">
                <div className="flex items-center">
                  <div className="bg-amber-100 p-3 rounded-full mr-4 group-hover:bg-amber-200 transition-colors">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-amber-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-800 text-lg group-hover:text-amber-800 transition-colors">
                    Discover more events
                  </span>
                </div>
                <span className="text-amber-600 text-xl group-hover:translate-x-1 transition-transform">
                  →
                </span>
              </div>
            </Link>
          </>
        )}
        {showForm && <EventForm onClose={() => setShowForm(false)} pe={true} />}
      </>
    );
  };

  return (
    <MainLayout
      rightSidebar={
        !isMobile ? (
          <HomeRightSidebar
            onClick={() => {
              if (isAuthenticated()) {
                setShowForm(true);
              } else {
                localStorage.setItem("target", window.location.pathname);
                navigate("/signin");
              }
            }}
          />
        ) : null
      }
    >
      {renderHomeContent()}
    </MainLayout>
  );
}

export default HomePage2;
